import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@mui/material';
import { Delete, Edit, Save, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { userXGroupValidator } from '../../validators';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';

const UserComponent = ({ index, existingUser, userList, onSave, onDelete, onCancel, onUpdate, canWrite }) => {
  const { t } = useTranslation();
  const [validation, validate] = useDirtyFieldValidation(userXGroupValidator);
  const editing = useMemo(() => existingUser.editing || existingUser.isNew, [existingUser.editing, existingUser.isNew]);
  const disabled = useMemo(() => existingUser.disabled, [existingUser.disabled]);
  const userName = userList.find(item => item.id === existingUser.userId)?.name || '';

  const filterUserXGroups = useCallback(
    value => {
      const inputText = value?.toString()?.toUpperCase() || '';
      if (!inputText) return Promise.resolve([]);
      const filteredUserXGroups = userList?.filter(({ name }) => name.toUpperCase().includes(inputText));
      return Promise.resolve(filteredUserXGroups);
    },
    [userList]
  );

  const updateLocalUserXGroup = useCallback(
    (property, value) => {
      onUpdate(existingUser.userId, property, value);
    },
    [onUpdate, existingUser.userId]
  );

  const getLensValue = useCallback(
    () => ({
      ...existingUser,
      initialUserId: existingUser.userId
    }),
    [existingUser]
  );

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(getLensValue());
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const userXGroupValue = useMemo(() => userList?.find(({ id }) => (lens.userId |> get) === id), [lens.userId, userList]);

  const handleAddOrUpdate = () => {
    if (!validate(lens |> get)) return;
    onSave(lens |> get);
  };

  const handleCancelEdit = useCallback(() => {
    if (existingUser.isNew) onCancel();
    else {
      updateLocalUserXGroup('editing', false);
      resetLens(getLensValue());
    }
  }, [getLensValue, onCancel, resetLens, updateLocalUserXGroup, existingUser.isNew]);

  const handleEdit = useCallback(() => updateLocalUserXGroup('editing', true), [updateLocalUserXGroup]);

  return (
    <StyledTableRow hover key={index}>
      <StyledTableBodyCell colSpan={2}>
        {editing ? (
          <Autocomplete
            fullWidth
            value={userXGroupValue}
            onChange={value => (lens.userId |> set)(value.id)}
            required
            valueKey='id'
            labelKey='name'
            loadOptions={filterUserXGroups}
            label={t('UserGroups.Users.User')}
            error={!isValid(validation.userId)}
            helperText={getErrors(validation.userId)}
            disabled={disabled || !canWrite}
          />
        ) : (
          <>{userName}</>
        )}
      </StyledTableBodyCell>
      <StyledTableBodyCell align='right' colSpan={2}>
        {editing ? (
          <>
            <IconButton
              type='cancel'
              size='tiny'
              color='primary'
              variant='text'
              onClick={handleCancelEdit}
              tooltip={t('Cancel')}
              disabled={disabled || !canWrite}
            >
              <Close />
            </IconButton>
            <IconButton
              type='save'
              color='primary'
              size='tiny'
              variant='text'
              onClick={handleAddOrUpdate}
              tooltip={t('Save')}
              disabled={disabled || !isDirty}
            >
              <Save />
            </IconButton>
          </>
        ) : (
          <IconButton
            type='edit'
            color='primary'
            size='tiny'
            variant='text'
            onClick={handleEdit}
            tooltip={t('Edit')}
            disabled={disabled || !canWrite}
          >
            <Edit />
          </IconButton>
        )}
        {!existingUser.isNew && (
          <IconButton
            type='delete'
            color='primary'
            size='tiny'
            variant='text'
            onClick={() => onDelete(existingUser.userId)}
            tooltip={t('Delete')}
            disabled={disabled || !canWrite}
          >
            <Delete />
          </IconButton>
        )}
      </StyledTableBodyCell>
    </StyledTableRow>
  );
};

UserComponent.propTypes = {
  index: PropTypes.number,
  existingUser: PropTypes.object,
  userList: PropTypes.array,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onCancel: PropTypes.func,
  onUpdate: PropTypes.func,
  canWrite: PropTypes.bool
};

export default UserComponent;
